import { useState } from "react";
import { Loader2 } from "lucide-react";
import toast from "react-hot-toast";

import { Button } from "@/components/ui/base/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/base/dialog";
import { Switch } from "@/components/ui/base/switch";
import {
  MerchantsRepository,
  hasSeenTapToPayEducation,
} from "@/application/merchants/merchants_repository";

import type { UserProfile } from "../types";

const repo = new MerchantsRepository();

/**
 * Flips whether the Tap to Pay education guide is forced on this user.
 *
 * Behind a confirm because the merchant sees the consequence, not the admin:
 * the change only lands the next time they restart the app, so an accidental
 * flip is invisible here and confusing there.
 */
export function TapToPayEducationToggle({
  user,
  onRefresh,
}: {
  user: UserProfile;
  onRefresh: () => void;
}) {
  const [pendingValue, setPendingValue] = useState<boolean | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const seen = hasSeenTapToPayEducation(user);

  async function handleConfirm() {
    if (pendingValue === null) return;
    setSubmitting(true);
    try {
      await repo.setTapToPayEducationSeen(user.userId, pendingValue);
      toast.success(
        pendingValue
          ? "Education marked as seen — the merchant must restart the app to pick it up"
          : "Education marked as not seen — the guide will show on next app restart",
      );
      setPendingValue(null);
      onRefresh();
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to update education flag",
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      <Switch
        checked={seen}
        onCheckedChange={(next) => setPendingValue(next)}
        aria-label={`Tap to Pay education seen for ${user.email}`}
      />

      <Dialog
        open={pendingValue !== null}
        onOpenChange={(open) => {
          if (!open && !submitting) setPendingValue(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Mark education as {pendingValue ? "seen" : "not seen"}?
            </DialogTitle>
            <DialogDescription>
              {pendingValue
                ? `The Tap to Pay education guide will no longer be forced on ${user.email}. `
                : `The Tap to Pay education guide will be shown to ${user.email} again. `}
              The change takes effect the next time they restart the app.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              disabled={submitting}
              onClick={() => setPendingValue(null)}
            >
              Cancel
            </Button>
            <Button disabled={submitting} onClick={() => void handleConfirm()}>
              {submitting && <Loader2 className="animate-spin" />}
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
